import { useMemo, useState } from 'react';
import { CalendarFold, Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ScheduleCard } from '@/pages/schedules/components/schedule-card';
import { ResizeHandle } from '@/pages/schedules/components/resize-handle';
import { dayKey } from '@/pages/schedules/helpers/day-range';
import { useListRunDays } from '@/hooks/queries/schedules/use-list-run-days';
import type { OrgSchedule } from '@/types/schedules';

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface ScheduleListPanelProps {
  width: number;
  onResizeStart: (e: React.MouseEvent) => void;
  schedules: OrgSchedule[];
  isLoading: boolean;
  isError: boolean;
  selectedScheduleId: string | null;
  onSelectSchedule: (id: string | null) => void;
  selectedDate: Date | undefined;
  onSelectDate: (date: Date | undefined) => void;
  onCreate: () => void;
  onEdit: (schedule: OrgSchedule) => void;
  onScheduleDeleted: (id: string) => void;
}

export function ScheduleListPanel({
  width,
  onResizeStart,
  schedules,
  isLoading,
  isError,
  selectedScheduleId,
  onSelectSchedule,
  selectedDate,
  onSelectDate,
  onCreate,
  onEdit,
  onScheduleDeleted,
}: ScheduleListPanelProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Computed per render so the disabled range stays correct across midnight.
  const today = new Date();
  const [visibleMonth, setVisibleMonth] = useState(() => new Date());

  // Run-day dots come from a dedicated endpoint scoped to the visible month, so
  // they stay complete regardless of feed pagination or the selected-day filter.
  const monthRange = useMemo(() => {
    const from = new Date(
      visibleMonth.getFullYear(),
      visibleMonth.getMonth(),
      1,
    );
    from.setDate(from.getDate() - 7);
    const to = new Date(
      visibleMonth.getFullYear(),
      visibleMonth.getMonth() + 1,
      0,
    );
    to.setDate(to.getDate() + 7);
    to.setHours(23, 59, 59, 999);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [visibleMonth]);

  const runDaysQuery = useListRunDays({
    scheduleId: selectedScheduleId ?? undefined,
    dateFrom: monthRange.from,
    dateTo: monthRange.to,
    timezone: LOCAL_TZ,
  });

  const runDays = useMemo(
    () => new Set(runDaysQuery.data?.days ?? []),
    [runDaysQuery.data],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return schedules;
    return schedules.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.workflow_name?.toLowerCase().includes(q),
    );
  }, [schedules, search]);

  return (
    <div
      className='relative flex h-full shrink-0 flex-col bg-sidebar'
      style={{ width }}
    >
      <div className='flex h-13 items-center justify-between px-4 py-3'>
        <div className='flex items-center gap-2'>
          <CalendarFold className='size-4' />
          <span className='text-sm font-medium'>Schedule</span>
        </div>
        <div className='flex items-center gap-1'>
          <Button
            variant='ghost'
            size='icon'
            className='size-8'
            onClick={() => {
              setSearchOpen((v) => !v);
              setSearch('');
            }}
          >
            <Search className='size-4' />
          </Button>
          <Button
            variant='ghost'
            size='icon'
            className='size-8'
            onClick={onCreate}
          >
            <Plus className='size-4' />
          </Button>
        </div>
      </div>
      <Separator />

      <div className='px-2 py-2'>
        <Calendar
          mode='single'
          selected={selectedDate}
          onSelect={onSelectDate}
          month={visibleMonth}
          onMonthChange={setVisibleMonth}
          disabled={{ after: today }}
          className='w-full'
          classNames={{
            month_grid: 'w-full table-fixed border-collapse',
            weekday: 'pb-1 text-xs font-normal text-muted-foreground',
            day: '!size-auto p-0.5 text-center',
            day_button:
              'mx-auto flex aspect-square w-full max-w-9 items-center justify-center rounded-md text-sm font-normal hover:bg-accent',
            today: '[&>button]:bg-accent [&>button]:text-accent-foreground',
            // On a selected day the dot flips to the foreground color so it
            // stays visible against the primary-filled button.
            selected:
              '[&>button]:bg-primary [&>button]:text-primary-foreground [&>button:hover]:bg-primary [&>button]:after:!bg-primary-foreground',
          }}
          modifiers={{ hasRuns: (date) => runDays.has(dayKey(date)) }}
          modifiersClassNames={{
            // Dot sits just below the number inside the button.
            hasRuns:
              '[&>button]:relative [&>button]:after:pointer-events-none [&>button]:after:absolute [&>button]:after:bottom-1 [&>button]:after:left-1/2 [&>button]:after:size-1 [&>button]:after:-translate-x-1/2 [&>button]:after:rounded-full [&>button]:after:bg-primary',
          }}
        />
      </div>
      <Separator />

      <div className='flex items-center gap-2 px-3 py-2'>
        {searchOpen ? (
          <div className='relative w-full'>
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search schedules'
              className='h-8 pr-8'
            />
            <button
              type='button'
              className='absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground'
              onClick={() => {
                setSearchOpen(false);
                setSearch('');
              }}
            >
              <X className='size-4' />
            </button>
          </div>
        ) : (
          <button
            type='button'
            onClick={() => onSelectSchedule(null)}
            className={cn(
              'w-full rounded-md px-2 py-1.5 text-left text-sm font-medium transition-colors hover:bg-accent',
              selectedScheduleId === null && 'bg-accent',
            )}
          >
            All Schedules
          </button>
        )}
      </div>

      <div className='subtle-scrollbar min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 pb-3'>
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className='mx-1 my-1 h-12 rounded-md' />
          ))}
        {isError && (
          <p className='px-3 py-6 text-center text-sm text-muted-foreground'>
            Failed to load schedules.
          </p>
        )}
        {!isLoading && !isError && filtered.length === 0 && (
          <p className='px-3 py-6 text-center text-sm text-muted-foreground'>
            {search ? 'No schedules match your search.' : 'No schedules yet.'}
          </p>
        )}
        {filtered.map((schedule) => (
          <ScheduleCard
            key={schedule.id}
            schedule={schedule}
            selected={selectedScheduleId === schedule.id}
            onSelect={() => onSelectSchedule(schedule.id)}
            onEdit={() => onEdit(schedule)}
            onDeleted={() => onScheduleDeleted(schedule.id)}
          />
        ))}
      </div>

      <ResizeHandle onResizeStart={onResizeStart} />
    </div>
  );
}
